import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createSlots as _createSlots } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("div", { class: "w-full" }, "Add Chapter", -1)
const _hoisted_2 = { class: "space-y-2 mb-4 max-h-[calc(100vh-20rem)] overflow-auto" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", null, "Chapter", -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("span", null, "Link", -1)
const _hoisted_5 = { class: "hidden" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", null, "Image URLs", -1)
const _hoisted_7 = { class: "flex gap-0" }
const _hoisted_8 = {
  key: 0,
  class: "m-auto grid grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-2"
}
const _hoisted_9 = { class: "flex items-center justify-between gap-2" }
const _hoisted_10 = { class: "flex items-center gap-2" }
const _hoisted_11 = { class: "text-black" }
const _hoisted_12 = { class: "flex items-center gap-2" }

import { Button, Divider, Input, Modal, Switch, Textarea } from 'ant-design-vue';
import { HttpStatusCode } from 'axios';
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import {
	CopyOutlined,
	DeleteOutlined,
	LoadingOutlined,
	ReloadOutlined
} from '@ant-design/icons-vue';
import DropFile from '@/components/shared/DropFile.vue';
import ImageLoading from '@/components/shared/ImageLoading.vue';
import { Map } from '@/model/universal';
import { scrapingFile } from '@/repository/centralized';
import { createComicChapter } from '@/repository/comic';
import { scrapingComic, unscrapableSites } from '@/util/scraping';


export default /*#__PURE__*/_defineComponent({
  __name: 'AddChapter',
  props: {
    dropable: { type: Boolean },
    isOpened: { type: Boolean },
    onClose: { type: Function },
    comicUUID: {}
  },
  emits: ["onBeforeClose"],
  setup(__props: any, { emit }) {

const props = __props;





const store = useStore();

const isAddMode = ref(true);
const isAutoUpload = ref(true);
const isAutoReload = ref(false);
const timestamp = ref(new Date().getTime());
const isAdding = ref(false);
const isScraping = ref(false);
const isValidLink = ref(false);
const images = ref<Map<File>>({});
const imageURLs = ref<Map<string>>({});
const tempImages = ref<{ imageURL: string; referer: string }[]>([]);
const imageURLsText = ref('');

const form = ref({
	chapter: 0,
	link: ''
});

watch(
	() => props.isOpened,
	(isOpened) => {
		if (isOpened) {
			timestamp.value = new Date().getTime();
		}
	}
);

async function extractImageURLs() {
	if (!imageURLsText.value) {
		return;
	}
	const urls = imageURLsText.value.split('\n');
	if (!isAddMode.value) {
		images.value = {};
		imageURLs.value = {};
		tempImages.value = [];
	}
	isScraping.value = true;
	const length = Object.keys(imageURLs.value).length;
	try {
		await Promise.all(
			urls.map(async (url, index) => {
				const { data } = await scrapingFile({ fileURL: url, fetchFromURL: form.value.link });
				const imageIndex = !isAddMode.value ? index : length + index;
				imageURLs.value[imageIndex] = URL.createObjectURL(data);
				images.value[imageIndex] = new File([data], `file${imageIndex}`);
			})
		);
		imageURLsText.value = '';
	} catch (error) {
		store.dispatch('alert', {
			type: 'error',
			message: `${error}` || 'something went wrong, please try again!'
		});
	} finally {
		isScraping.value = false;
	}
}

async function extractLink() {
	const currentTimestamp = timestamp.value;
	if (!isAutoUpload.value) {
		isValidLink.value = true;
		return;
	}
	if (props.dropable) {
		try {
			const host = new URL(form.value.link).origin;
			if (unscrapableSites.includes(host)) {
				isValidLink.value = true;
				return;
			}
		} catch (error) {
			return;
		}
	}
	if (!isAddMode.value) {
		images.value = {};
		imageURLs.value = {};
		tempImages.value = [];
	}
	isScraping.value = true;
	const length = Object.keys(imageURLs.value).length;
	try {
		let retryCount = 5;
		let round = 0;

		if (isAutoReload.value) {
			const retryReloadCount = 5;
			let reloadCount = 0;
			const retryReload = () => {
				setTimeout(() => {
					if (reloadCount < retryReloadCount) {
						reload();
						reloadCount++;
						retryReload();
					}
				}, 10000);
			};
			setTimeout(() => {
				retryReload();
			}, 5000);
		}

		while (round < retryCount) {
			try {
				await scrapingComic(form.value.link, (file, index, _, referer, imageURL) => {
					if (timestamp.value === currentTimestamp && props.isOpened) {
						const imageIndex = !isAddMode.value ? index : length + index;
						imageURLs.value[imageIndex] = URL.createObjectURL(file);
						images.value[imageIndex] = file;
						tempImages.value.push({ referer, imageURL });
					}
				});
				break;
			} catch (error) {
				round++;
			}
		}
		if (round === retryCount) {
			throw Error('Unable to extract comic from link');
		}
		isValidLink.value = timestamp.value === currentTimestamp && props.isOpened;
	} catch (error) {
		store.dispatch('alert', {
			type: 'error',
			message: `${error}` || 'something went wrong, please try again!'
		});
	} finally {
		if (timestamp.value === currentTimestamp) {
			isScraping.value = false;
			tempImages.value = [];
		}
	}
}

async function reload() {
	if (!isScraping.value) {
		return;
	}
	const currentTimestamp = timestamp.value;
	try {
		await scrapingComic(
			form.value.link,
			(file, index, _, referer, imageURL) => {
				if (timestamp.value === currentTimestamp && props.isOpened) {
					imageURLs.value[index] = URL.createObjectURL(file);
					images.value[index] = file;
					tempImages.value.push({ referer, imageURL });
				}
			},
			tempImages.value.map((image) => image.imageURL)
		);
		isValidLink.value = timestamp.value === currentTimestamp && props.isOpened;
	} catch (error) {
		store.dispatch('alert', {
			type: 'error',
			message: `${error}` || 'something went wrong, please try again!'
		});
	} finally {
		isScraping.value = false;
		tempImages.value = [];
	}
}

async function submit() {
	if (
		!images.value ||
		`${form.value.chapter}`.length === 0 ||
		form.value.link.length === 0 ||
		!isValidLink.value ||
		isAdding.value ||
		isScraping.value ||
		Object.keys(images.value).length === 0
	) {
		return;
	}
	try {
		isAdding.value = true;
		const files: File[] = [];
		const sortIndex = Object.keys(images.value).sort((a, b) => Number(a) - Number(b));
		for (let index of sortIndex) {
			files.push(images.value[index]);
		}
		const res = await createComicChapter(
			props.comicUUID,
			form.value.chapter.toString(),
			form.value.link,
			files
		);
		if (res.status !== HttpStatusCode.Ok) {
			throw Error(
				res.status === HttpStatusCode.Conflict
					? 'This chapter has been added'
					: 'Something went wrong, please try again!'
			);
		}
		await emit('onBeforeClose');
		store.dispatch('alert', { type: 'success', message: 'Add new chapter successfully!' });
		close();
	} catch (error) {
		store.dispatch('alert', {
			type: 'error',
			message: `${error}` || 'something went wrong, please try again!'
		});
	} finally {
		isAdding.value = false;
	}
}

function dropFiles(files: File[]) {
	if (isAdding.value || isScraping.value) {
		return;
	}
	files.forEach((file, index) => {
		const length = Object.keys(images.value).length;
		images.value[length + index + 1] = file;
		imageURLs.value[length + index + 1] = URL.createObjectURL(file);
	});
}

function removeFile(removeIndex: number | string) {
	delete images.value[removeIndex];
	delete imageURLs.value[removeIndex];
}

function close() {
	isValidLink.value = false;
	isAdding.value = false;
	isScraping.value = false;
	images.value = {};
	imageURLs.value = {};
	tempImages.value = [];
	form.value = {
		chapter: 0,
		link: ''
	};
	props.onClose();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Modal), {
    open: _ctx.isOpened,
    closable: false
  }, {
    title: _withCtx(() => [
      _hoisted_1,
      _createVNode(_unref(Divider), { class: "p-0 my-4" })
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(Divider), { class: "p-0 my-4" }),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("div", _hoisted_10, [
          _createVNode(_unref(Switch), {
            checkedChildren: "Add",
            unCheckedChildren: "Replace",
            checked: isAddMode.value,
            "onUpdate:checked": _cache[4] || (_cache[4] = ($event: any) => ((isAddMode).value = $event))
          }, null, 8, ["checked"]),
          _createVNode(_unref(Switch), {
            checkedChildren: "Import",
            unCheckedChildren: "Upload",
            checked: isAutoUpload.value,
            "onUpdate:checked": _cache[5] || (_cache[5] = ($event: any) => ((isAutoUpload).value = $event))
          }, null, 8, ["checked"]),
          _createVNode(_unref(Switch), {
            checkedChildren: "Reload",
            unCheckedChildren: "Manual",
            checked: isAutoReload.value,
            "onUpdate:checked": _cache[6] || (_cache[6] = ($event: any) => ((isAutoReload).value = $event))
          }, null, 8, ["checked"]),
          _createVNode(_unref(ReloadOutlined), {
            onClick: reload,
            disabled: !isScraping.value || isAdding.value || Object.keys(images.value).length === 0
          }, null, 8, ["disabled"]),
          _createElementVNode("div", _hoisted_11, _toDisplayString(Object.keys(images.value).length), 1)
        ]),
        _createElementVNode("div", _hoisted_12, [
          _createVNode(_unref(Button), {
            key: "back",
            onClick: close,
            disabled: isAdding.value
          }, {
            default: _withCtx(() => [
              _createTextVNode("Cancel")
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(_unref(Button), {
            key: "submit",
            type: "primary",
            disabled: 
							`${form.value.chapter}`.length === 0 ||
							form.value.link.length === 0 ||
							!isValidLink.value ||
							isAdding.value ||
							isScraping.value ||
							Object.keys(images.value).length === 0
						,
            onClick: submit
          }, _createSlots({
            default: _withCtx(() => [
              _createTextVNode(" OK ")
            ]),
            _: 2
          }, [
            (isAdding.value)
              ? {
                  name: "icon",
                  fn: _withCtx(() => [
                    _createVNode(_unref(LoadingOutlined))
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1032, ["disabled"])
        ])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", null, [
          _hoisted_3,
          _createVNode(_unref(Input), {
            type: "number",
            value: (+form.value.chapter).toString(),
            min: 0,
            max: 999,
            disabled: isAdding.value,
            onChange: _cache[0] || (_cache[0] = ($event: any) => (form.value.chapter = +$event.target.value!))
          }, null, 8, ["value", "disabled"])
        ]),
        _createElementVNode("div", null, [
          _hoisted_4,
          _createVNode(_unref(Input).Group, { compact: "" }, {
            default: _withCtx(() => [
              _createVNode(_unref(Input), {
                value: form.value.link,
                "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((form.value.link) = $event)),
                class: "!w-[calc(100%-2rem)] !text-start",
                placeholder: "Enter link",
                onChange: _cache[2] || (_cache[2] = () => (isValidLink.value = false)),
                disabled: isAdding.value
              }, null, 8, ["value", "disabled"]),
              _createVNode(_unref(Button), {
                onClick: extractLink,
                disabled: isAdding.value
              }, {
                icon: _withCtx(() => [
                  (isScraping.value)
                    ? (_openBlock(), _createBlock(_unref(LoadingOutlined), { key: 0 }))
                    : (_openBlock(), _createBlock(_unref(CopyOutlined), { key: 1 }))
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            _: 1
          })
        ]),
        _createElementVNode("div", _hoisted_5, [
          _hoisted_6,
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_unref(Textarea), {
              placeholder: "Input image url separate by new line",
              autoSize: "",
              allowClear: "",
              value: imageURLsText.value,
              onInput: _cache[3] || (_cache[3] = ($event: any) => (imageURLsText.value = $event.target.value || '')),
              disabled: isAdding.value
            }, null, 8, ["value", "disabled"]),
            _createVNode(_unref(Button), {
              onClick: extractImageURLs,
              disabled: isAdding.value
            }, {
              default: _withCtx(() => [
                _createTextVNode(" OK ")
              ]),
              _: 1
            }, 8, ["disabled"])
          ])
        ]),
        (_ctx.dropable || Object.keys(imageURLs.value).length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              (_ctx.dropable)
                ? (_openBlock(), _createBlock(DropFile, {
                    key: 0,
                    onSelect: dropFiles
                  }))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(imageURLs.value, (image, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "relative min-w-[8rem] min-h-[8rem] aspect-square overflow-hidden rounded-md",
                  key: index
                }, [
                  _createVNode(ImageLoading, {
                    src: image,
                    previewable: "",
                    "img-class": "min-w-[8rem] min-h-[8rem] shadow-md rounded-md aspect-square",
                    "loading-class": "min-w-[8rem] min-h-[8rem] shadow-md flex items-start rounded-md"
                  }, {
                    default: _withCtx(() => [
                      (_ctx.dropable)
                        ? (_openBlock(), _createBlock(_unref(Button), {
                            key: 0,
                            onClick: ($event: any) => (removeFile(index)),
                            class: "bg-red-400 border-none hover:!text-white rounded-full absolute top-0 right-0",
                            disabled: isAdding.value || isScraping.value
                          }, {
                            icon: _withCtx(() => [
                              _createVNode(_unref(DeleteOutlined))
                            ]),
                            _: 2
                          }, 1032, ["onClick", "disabled"]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 2
                  }, 1032, ["src"])
                ]))
              }), 128))
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["open"]))
}
}

})